import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchContractRequestList } from '../services/contractRequestService';
import { fetchCampaignByRequestId } from '../services/campaignContentService';
import { fetchContentItems } from '../services/campaignContentService';

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'REQUESTED', label: '신청' },
  { value: 'APPROVED', label: '승인' },
  { value: 'REJECTED', label: '반려' }
];

const STATUS_LABEL = {
  REQUESTED: '신청',
  APPROVED: '승인',
  REJECTED: '반려'
};

const CANCEL_LABEL = {
  NONE: '-',
  REQUESTED: '취소 요청됨',
  APPROVED: '취소 확정',
  REJECTED: '취소 거절',
  WITHDRAWN: '요청 철회'
};

const toLocalText = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR');
};

const parseYmd = (ymd) => {
  const raw = typeof ymd === 'string' ? ymd.trim() : '';
  if (!raw) return null;
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = Number(m[1]);
  const mm = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mm - 1, d);
  return Number.isFinite(dt.getTime()) ? dt : null;
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const resolveCampaignPhase = (sellStartDate, sellEndDate) => {
  const start = parseYmd(sellStartDate);
  const end = parseYmd(sellEndDate);
  if (!start || !end) return 'UNKNOWN';
  const today = startOfToday();
  const endInclusive = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);
  if (today < start) return 'BEFORE';
  if (today > endInclusive) return 'ENDED';
  return 'ONGOING';
};

const normalizeContentItemStatus = (value) => {
  const s = String(value || '').trim().toUpperCase();
  if (!s) return 'UNKNOWN';
  if (s === 'CREATED') return 'CREATED';
  if (s === 'DRAFT') return 'DRAFT';
  if (s === 'SUBMITTED') return 'SUBMITTED';
  if (s === 'APPROVED') return 'APPROVED';
  if (s === 'PUBLISHED') return 'PUBLISHED';
  if (s === 'REJECTED') return 'REJECTED';
  if (s === 'REVISION_REQUESTED') return 'REVISION_REQUESTED';
  return 'UNKNOWN';
};

const normalizeUrlStatus = (value) => {
  const v = String(value || '').trim().toUpperCase();
  if (v === 'NONE') return 'NONE';
  if (v === 'SUBMITTED') return 'SUBMITTED';
  if (v === 'APPROVED') return 'APPROVED';
  if (v === 'REJECTED') return 'REJECTED';
  return 'UNKNOWN';
};

const resolveNextActionMessage = ({ row, readiness, items }) => {
  const status = String(row?.status || '').trim().toUpperCase();
  const cancelStatus = String(row?.cancelStatus || 'NONE').trim().toUpperCase();
  if (cancelStatus === 'REQUESTED') return '취소 요청이 처리중입니다.';
  if (cancelStatus === 'APPROVED') return '취소가 승인되었습니다.';

  if (status === 'REJECTED') {
    const note = String(row?.reviewNote || '').trim();
    return note ? `계약이 반려되었습니다. 사유: ${note}` : '계약이 반려되었습니다. 반려 사유를 확인하세요.';
  }

  const pending = String(row?.conditionPendingSide || '').trim().toUpperCase();
  if (status === 'REQUESTED' || status === 'REVIEWED') {
    if (pending === 'TRP') return '계약 조건을 확인하고 승인 또는 반려를 진행하세요.';
    return '상대방의 계약 조건 승인을 기다리고 있습니다.';
  }
  if (status !== 'APPROVED') return '-';

  const trpSignedAt = String(row?.trpSignedAt || '').trim();
  const crsSignedAt = String(row?.crsSignedAt || '').trim();
  const isTrpSigned = Boolean(trpSignedAt);
  const isCrsSigned = Boolean(crsSignedAt);
  if (!isTrpSigned || !isCrsSigned) {
    if (!isTrpSigned) return '계약서에 서명하세요.';
    return '상대방의 계약서 서명을 기다리고 있습니다.';
  }

  if (!readiness?.campaign?.campaignId) return '계약이 승인되었습니다. 캠페인을 생성하세요.';

  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return '인플루언서가 캠페인 기획안을 작성중입니다.';

  const firstContentSubmitted = list.find((it) => normalizeContentItemStatus(it?.status) === 'SUBMITTED');
  if (firstContentSubmitted) return '콘텐츠 기획안이 제출되었습니다. 확인 후 승인 또는 수정요청을 진행하세요.';

  const firstUrlSubmitted = list.find((it) => normalizeUrlStatus(it?.urlStatus) === 'SUBMITTED');
  if (firstUrlSubmitted) return 'URL이 제출되었습니다. 확인 후 승인 또는 반려를 진행하세요.';

  if (list.some((it) => normalizeContentItemStatus(it?.status) === 'REVISION_REQUESTED')) {
    return '콘텐츠 수정요청을 보냈습니다. 인플루언서의 수정 제출을 기다려주세요.';
  }

  if (list.some((it) => normalizeUrlStatus(it?.urlStatus) === 'REJECTED')) {
    return 'URL을 반려했습니다. 인플루언서의 URL 재제출을 기다려주세요.';
  }

  const needUrl = list.some((it) => {
    const s = normalizeContentItemStatus(it?.status);
    const u = normalizeUrlStatus(it?.urlStatus);
    return (s === 'APPROVED' || s === 'PUBLISHED') && (u === 'NONE' || u === 'UNKNOWN');
  });
  if (needUrl) return '인플루언서가 SNS게시 후 URL 등록 전 입니다.';

  const drafting = list.some((it) => {
    const s = normalizeContentItemStatus(it?.status);
    return s === 'CREATED' || s === 'DRAFT';
  });
  if (drafting) return '인플루언서가 캠페인 기획안을 작성중입니다.';

  const allUrlApproved = list.length > 0 && list.every((it) => normalizeUrlStatus(it?.urlStatus) === 'APPROVED');
  if (allUrlApproved) return '모든 절차가 마무리 되었습니다';

  return '-';
};

const isGroupRow = (row) => {
  const t = row?.__type;
  return typeof t === 'string' && t.startsWith('group_');
};

const resolveAggregatedContentLabel = ({ readiness, items, row }) => {
  if (!readiness) return '-';
  
  // 계약서 작성 단계 확인
  if (row) {
    const trpSigned = !!(row?.trpSignedByUserNo && row?.trpSignedAt);
    const crsSigned = !!(row?.crsSignedByUserNo && row?.crsSignedAt);
    
    // 계약 상태가 승인되었지만 서명이 완료되지 않은 경우
    if (String(row?.status || '').toUpperCase() === 'APPROVED' && (!trpSigned || !crsSigned)) {
      return '계약서 작성중';
    }
  }
  
  const deliverables = Array.isArray(readiness?.deliverables) ? readiness.deliverables : [];
  if (deliverables.length === 0) return '미설정';

  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return '-';

  const statuses = list.map((it) => normalizeContentItemStatus(it?.status));

  if (statuses.some((s) => s === 'REVISION_REQUESTED' || s === 'REJECTED')) return '기획안 수정요청';
  if (statuses.some((s) => s === 'SUBMITTED')) return '기획안 검토중';
  if (statuses.some((s) => s === 'CREATED' || s === 'DRAFT')) return '기획안 작성중';
  if (statuses.every((s) => s === 'APPROVED' || s === 'PUBLISHED')) return '기획안 승인';
  if (statuses.some((s) => s === 'APPROVED' || s === 'PUBLISHED')) return '기획안 승인';
  return '-';
};

const ContractStatusBadge = ({ status }) => {
  const s = String(status || '').toUpperCase();
  const label = STATUS_LABEL[s] || String(status || '-');
  const cls =
    s === 'APPROVED'
      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
      : s === 'REJECTED'
        ? 'bg-red-50 text-red-800 border-red-200'
        : 'bg-blue-50 text-blue-800 border-blue-200';
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{label}</span>;
};

const CancelStatusBadge = ({ cancelStatus }) => {
  const s = String(cancelStatus || 'NONE').toUpperCase();
  if (s === 'NONE') return null;
  const label = CANCEL_LABEL[s] || '-';
  const cls =
    s === 'REQUESTED'
      ? 'bg-amber-50 text-amber-800 border-amber-200'
      : s === 'APPROVED'
        ? 'bg-gray-100 text-gray-700 border-gray-200'
        : s === 'REJECTED'
          ? 'bg-red-50 text-red-800 border-red-200'
          : 'bg-gray-50 text-gray-600 border-gray-200';
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{label}</span>;
};

const CampaignPhaseBadge = ({ phase }) => {
  const p = String(phase || 'UNKNOWN').toUpperCase();
  if (p === 'BEFORE') {
    return <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold bg-slate-50 text-slate-700 border-slate-200">진행전</span>;
  }
  if (p === 'ONGOING') {
    return <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold bg-indigo-50 text-indigo-800 border-indigo-200">진행중</span>;
  }
  if (p === 'ENDED') {
    return <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold bg-gray-100 text-gray-700 border-gray-200">완료</span>;
  }
  return <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold bg-gray-50 text-gray-600 border-gray-200">-</span>;
};

const ContentProgressBadge = ({ label }) => {
  const v = String(label || '-').trim();
  const cls =
    v === '기획안 수정요청'
      ? 'bg-red-50 text-red-800 border-red-200'
      : v === '기획안 검토중'
        ? 'bg-amber-50 text-amber-800 border-amber-200'
        : v === '기획안 승인'
          ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
          : v === '미설정'
            ? 'bg-gray-50 text-gray-600 border-gray-200'
            : v === '기획안 작성중'
              ? 'bg-gray-100 text-gray-700 border-gray-200'
              : 'bg-gray-50 text-gray-600 border-gray-200';
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{v || '-'}</span>;
};

const ContractListPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [collapsedProducts, setCollapsedProducts] = useState(() => new Set());
  const [campaignByRequestId, setCampaignByRequestId] = useState({});
  const [contentItemsByCampaignId, setContentItemsByCampaignId] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');

  const toggleProductCollapsed = (productNo) => {
    const key = String(productNo || '').trim();
    if (!key) return;
    setCollapsedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const list = await fetchContractRequestList({
        status: statusFilter,
        requestCompanyNo: companyFilter ? Number(companyFilter) : '',
        productNo: productFilter ? Number(productFilter) : ''
      });

      const filtered = list.filter((row) => {
        const status = String(row?.status || '').toUpperCase();
        const cancelStatus = String(row?.cancelStatus || 'NONE').toUpperCase();
        const isPending = status === 'REQUESTED' || status === 'REVIEWED';
        const isCancelFlow = cancelStatus === 'REQUESTED' || cancelStatus === 'APPROVED';
        if (isPending && isCancelFlow) return false;
        return true;
      });

      setRows(filtered);
    } catch (e) {
      setError(e?.message || '계약 요청 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    const requestIds = (Array.isArray(rows) ? rows : [])
      .map((r) => Number(r?.requestId))
      .filter((n) => Number.isFinite(n) && n > 0);
    const missing = requestIds.filter((id) => !(id in (campaignByRequestId || {})));
    if (missing.length === 0) return;

    let mounted = true;
    const run = async () => {
      const pairs = await Promise.all(
        missing.map(async (requestId) => {
          try {
            const readiness = await fetchCampaignByRequestId(requestId);
            return [requestId, readiness];
          } catch {
            return [requestId, null];
          }
        })
      );
      if (!mounted) return;
      setCampaignByRequestId((prev) => {
        const next = { ...(prev || {}) };
        for (const [requestId, readiness] of pairs) {
          next[requestId] = readiness;
        }
        return next;
      });
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [rows, campaignByRequestId]);

  useEffect(() => {
    const readinessList = Object.values(campaignByRequestId || {}).filter(Boolean);
    const campaignIds = readinessList
      .map((r) => Number(r?.campaign?.campaignId))
      .filter((n) => Number.isFinite(n) && n > 0);
    const unique = Array.from(new Set(campaignIds));
    const missing = unique.filter((id) => !(id in (contentItemsByCampaignId || {})));
    if (missing.length === 0) return;

    let mounted = true;
    const run = async () => {
      const pairs = await Promise.all(
        missing.map(async (campaignId) => {
          try {
            const items = await fetchContentItems(campaignId);
            return [campaignId, items];
          } catch {
            return [campaignId, []];
          }
        })
      );
      if (!mounted) return;
      setContentItemsByCampaignId((prev) => {
        const next = { ...(prev || {}) };
        for (const [campaignId, items] of pairs) {
          next[campaignId] = items;
        }
        return next;
      });
    };

    void run();
    return () => {
      mounted = false;
    };
  }, [campaignByRequestId, contentItemsByCampaignId]);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold text-gray-900">계약관리</h1>
        <button
          type="button"
          className="px-3 py-1.5 rounded border border-gray-300 text-sm hover:bg-gray-50"
          onClick={() => void load()}
          disabled={loading}
        >
          새로고침
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
        <select
          className="border rounded px-3 py-2 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          className="border rounded px-3 py-2 text-sm"
          placeholder="신청 업체번호"
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value.replace(/[^0-9]/g, ''))}
        />
        <input
          className="border rounded px-3 py-2 text-sm"
          placeholder="상품번호"
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value.replace(/[^0-9]/g, ''))}
        />
        <button
          type="button"
          className="px-3 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
          onClick={() => void load()}
          disabled={loading}
        >
          조회
        </button>
      </div>

      {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

      <div className="rounded-md overflow-auto bg-white">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">신청업체</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">계약상태</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">콘텐츠상태</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">캠페인상태</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">지금 해야 할 일</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-sm text-gray-500">
                  로딩 중...
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-sm text-gray-500">
                  조회된 요청이 없습니다.
                </td>
              </tr>
            )}
            {!loading && (() => {
              const list = Array.isArray(rows) ? rows : [];
              const byProduct = new Map();
              for (const r of list) {
                const pno = String(r?.productNo ?? '').trim();
                const key = pno || '0';
                if (!byProduct.has(key)) byProduct.set(key, []);
                byProduct.get(key).push(r);
              }

              const ordered = Array.from(byProduct.entries()).sort((a, b) => {
                const an = Number(a[0]);
                const bn = Number(b[0]);
                if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return bn - an;
                return String(a[0]).localeCompare(String(b[0]));
              });

              const displayRows = [];
              for (const [productNoKey, groupRows] of ordered) {
                const productNo = productNoKey === '0' ? '' : productNoKey;
                const first = Array.isArray(groupRows) && groupRows.length > 0 ? groupRows[0] : null;
                const productName = String(first?.productName || '').trim() || '-';
                const groupKey = String(productNoKey);
                displayRows.push({ __type: 'group_product', __key: groupKey, productNo, productName, count: groupRows.length });
                if (collapsedProducts.has(groupKey)) continue;
                const sorted = (Array.isArray(groupRows) ? [...groupRows] : []).sort((x, y) => Number(y?.requestId || 0) - Number(x?.requestId || 0));
                for (const r of sorted) displayRows.push({ ...r, __indent: 'under_product' });
              }

              return displayRows.map((row) => {
                if (isGroupRow(row)) {
                  const key = String(row?.__key || '');
                  const collapsed = collapsedProducts.has(key);
                  const label = row?.productNo ? `${row.productName} (#${row.productNo})` : `${row.productName}`;
                  return (
                    <tr key={`group:${key}`} className="bg-white">
                      <td colSpan={6} className="px-3 py-2">
                        <button
                          type="button"
                          className="w-full text-left flex items-center gap-2 px-2 py-1.5 font-semibold text-gray-800 rounded-md bg-gray-50/60 hover:bg-gray-100"
                          onClick={() => toggleProductCollapsed(key)}
                        >
                          <span className="inline-flex items-center justify-center w-4 h-4 text-gray-500">
                            {collapsed ? (
                              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L10.94 10 7.23 6.29a.75.75 0 1 1 1.06-1.06l4.24 4.24a.75.75 0 0 1 0 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.25 8.27a.75.75 0 0 1-.02-1.06z" clipRule="evenodd" />
                              </svg>
                            )}
                          </span>
                          <span className="truncate">{label}</span>
                          <span className="ml-auto text-xs font-semibold text-gray-500">{Number(row?.count || 0)}건</span>
                        </button>
                      </td>
                    </tr>
                  );
                }

                const requesterCompany = String(row.requestCompanyName || '').trim() || String(row.requestCompanyNo || '');
                const requesterUser = String(row.requestUserName || '').trim();
                const requesterText = requesterUser ? `${requesterCompany} / ${requesterUser}` : requesterCompany;
                const requestId = Number(row?.requestId);
                const readiness = Number.isFinite(requestId) ? (campaignByRequestId || {})[requestId] : null;
                const fromCampaign = readiness?.campaign || null;
                const campaignId = Number(fromCampaign?.campaignId);
                const contentItems = Number.isFinite(campaignId) && campaignId > 0 ? (contentItemsByCampaignId || {})[campaignId] : [];
                const contentLabel = resolveAggregatedContentLabel({ readiness, items: contentItems, row });
                const cond = row?.requestedSalesCondition && typeof row.requestedSalesCondition === 'object' ? row.requestedSalesCondition : {};
                const phase = resolveCampaignPhase(fromCampaign?.sellStartDate || cond?.sellStartDate, fromCampaign?.sellEndDate || cond?.sellEndDate);
                const nextActionMessage = resolveNextActionMessage({ row, readiness, items: contentItems });
                const indentClass = row?.__indent === 'under_product' ? 'pl-7' : '';

                return (
                  <tr
                    key={row.requestId}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/contracts/${row.requestId}`)}
                  >
                    <td className={`px-3 py-2 text-sm ${indentClass}`}>{requesterText}</td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <ContractStatusBadge status={row.status} />
                        <CancelStatusBadge cancelStatus={row.cancelStatus} />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm"><ContentProgressBadge label={contentLabel} /></td>
                    <td className="px-3 py-2 text-sm"><CampaignPhaseBadge phase={phase} /></td>
                    <td className="px-3 py-2 text-sm text-gray-700">{nextActionMessage}</td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContractListPage;
